import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from "recharts";
import {
  ArrowLeft, DollarSign, Users, Calendar, TrendingUp, TrendingDown, AlertCircle,
  Loader2, BarChart3, Wallet, Clock,
} from "lucide-react";
import { useT, useLang } from "@/lib/i18n";

const formatCurrency = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatMonth = (ym, locale = "en-US") => {
  if (!ym) return ym;
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString(locale, { month: "short", year: "2-digit" });
};

const SOURCE_COLORS = {
  qr_code: "#8B1C22", website: "#C4A252", facebook: "#3B82F6",
  referral: "#15803D", walk_in: "#A855F7", other: "#737373",
};

const KpiCard = ({ icon: Icon, label, value, sub, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <div className="flex items-start justify-between">
      <Icon className="w-6 h-6 text-[#C4A252]" />
    </div>
    <div className={`mt-3 text-3xl font-serif-display ${accent}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
    {sub && <div className="text-[10px] text-[#737373] mt-1">{sub}</div>}
  </div>
);

const RevenueDashboard = () => {
  const t = useT();
  const { lang } = useLang();
  const locale = lang === "es" ? "es-US" : "en-US";
  const fmtMonth = (ym) => formatMonth(ym, locale);
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/revenue/overview"); setData(data); }
    catch { toast.error(t("admin.revenue.toast.load_failed")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  if (loading) {
    return <div className="min-h-screen flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-[#C4A252]" /></div>;
  }
  if (!data) return null;

  const monthlyChange = data.monthly_revenue.change_pct;
  const ChangeIcon = monthlyChange >= 0 ? TrendingUp : TrendingDown;

  return (
    <div className="rn3-container py-10" data-testid="revenue-dashboard">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="revenue-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.back_to_admin")}
      </button>

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow"><BarChart3 className="w-3 h-3 inline mr-1" /> {t("admin.revenue.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-2">{t("admin.revenue.heading")}</h1>
        </div>
        <div className="text-xs text-[#737373]">{t("admin.revenue.as_of")} {new Date(data.as_of).toLocaleString()}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-8">
        <KpiCard icon={Users} label={t("admin.revenue.kpi.total_clients")} value={data.total_clients} testid="kpi-clients" />
        <KpiCard
          icon={DollarSign}
          label={t("admin.revenue.kpi.monthly")}
          value={formatCurrency(data.monthly_revenue.current_month)}
          sub={
            <span className={`inline-flex items-center gap-0.5 ${monthlyChange >= 0 ? "text-[#15803D]" : "text-[#8B1C22]"}`}>
              <ChangeIcon className="w-3 h-3" /> {Math.abs(monthlyChange)}% {t("admin.revenue.vs_last_month")}
            </span>
          }
          testid="kpi-monthly"
        />
        <KpiCard icon={AlertCircle} label={t("admin.revenue.kpi.pending")} value={formatCurrency(data.pending_revenue)} accent="text-[#8B1C22]" sub={t("admin.revenue.kpi.pending_sub")} testid="kpi-pending" />
        <KpiCard icon={Wallet} label={t("admin.revenue.kpi.collected_mtd")} value={formatCurrency(data.collected_revenue.mtd)} accent="text-[#15803D]" sub={`${t("admin.revenue.kpi.ytd")} ${formatCurrency(data.collected_revenue.ytd)}`} testid="kpi-collected" />
        <KpiCard icon={Calendar} label={t("admin.revenue.kpi.upcoming_appts")} value={data.appointments.upcoming} sub={`${data.appointments.total} ${t("admin.revenue.kpi.total_lower")}`} testid="kpi-appts" />
        <KpiCard icon={TrendingUp} label={t("admin.revenue.kpi.lead_conversion")} value={`${data.lead_conversion.pct}%`} sub={`${data.lead_conversion.converted} / ${data.lead_conversion.total_leads} ${t("admin.revenue.leads_lower")}`} testid="kpi-conversion" />
        <KpiCard icon={DollarSign} label={t("admin.revenue.kpi.alltime")} value={formatCurrency(data.collected_revenue.all_time)} testid="kpi-alltime" />
      </div>

      {/* Trend chart */}
      <div className="mt-10 bg-white border border-[#E5E5E5] p-5" data-testid="revenue-trend">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="rn3-eyebrow">{t("admin.revenue.trend.eyebrow")}</div>
            <h2 className="font-serif-display text-2xl mt-1">{t("admin.revenue.trend.heading")}</h2>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#737373]">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#8B1C22]" /> {t("admin.revenue.legend.billed")}</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#15803D]" /> {t("admin.revenue.legend.collected")}</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart data={data.monthly_revenue.series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-billed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B1C22" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#8B1C22" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="grad-collected" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#15803D" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#15803D" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F1F1F1" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#A3A3A3" tick={{ fontSize: 10, fill: "#737373" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={formatCurrency} stroke="#A3A3A3" tick={{ fontSize: 10, fill: "#737373" }} tickLine={false} axisLine={false} width={60} />
              <Tooltip
                labelFormatter={fmtMonth}
                contentStyle={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 0, fontSize: 12 }}
                formatter={(v, k) => [formatCurrency(v), t(`admin.revenue.legend.${k}`)]}
              />
              <Area type="monotone" dataKey="billed" stroke="#8B1C22" strokeWidth={1.5} fill="url(#grad-billed)" isAnimationActive={false} />
              <Area type="monotone" dataKey="collected" stroke="#15803D" strokeWidth={1.5} fill="url(#grad-collected)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        {/* Revenue by Service */}
        <div className="bg-white border border-[#E5E5E5] p-5" data-testid="revenue-by-service">
          <div className="rn3-eyebrow">{t("admin.revenue.by_service.eyebrow")}</div>
          <h2 className="font-serif-display text-2xl mt-1 mb-4">{t("admin.revenue.by_service.heading")}</h2>
          {data.revenue_by_service.length === 0 ? (
            <div className="text-xs text-[#737373] italic py-4">{t("admin.revenue.by_service.empty")}</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={data.revenue_by_service} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#F1F1F1" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={formatCurrency} stroke="#A3A3A3" tick={{ fontSize: 10, fill: "#737373" }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="label" stroke="#A3A3A3" tick={{ fontSize: 11, fill: "#0A0A0A" }} tickLine={false} axisLine={false} width={120} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 0, fontSize: 12 }}
                      formatter={(v, k) => [formatCurrency(v), t(`admin.revenue.legend.${k}`)]}
                    />
                    <Bar dataKey="billed" fill="#8B1C22" />
                    <Bar dataKey="collected" fill="#15803D" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {data.revenue_by_service.map((s) => (
                  <div key={s.service} className="flex justify-between border-b border-[#F3EFE9] py-1" data-testid={`service-row-${s.service}`}>
                    <span className="text-[#0A0A0A]">{s.label}</span>
                    <span className="text-[#737373]">
                      <span className="font-semibold text-[#0A0A0A]">{formatCurrency(s.billed)}</span> {t("admin.revenue.billed_lower")} · {formatCurrency(s.collected)} {t("admin.revenue.collected_lower")} · {s.invoice_count} {t("admin.revenue.invoices_lower")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Lead Conversion by Source */}
        <div className="bg-white border border-[#E5E5E5] p-5" data-testid="conversion-by-source">
          <div className="rn3-eyebrow">{t("admin.revenue.by_source.eyebrow")}</div>
          <h2 className="font-serif-display text-2xl mt-1 mb-4">{t("admin.revenue.by_source.heading")}</h2>
          {data.lead_conversion.by_source.length === 0 ? (
            <div className="text-xs text-[#737373] italic py-4">{t("admin.revenue.by_source.empty")}</div>
          ) : (
            <>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={data.lead_conversion.by_source} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#F1F1F1" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="source" tickFormatter={(s) => s.replace(/_/g, " ")} stroke="#A3A3A3" tick={{ fontSize: 10, fill: "#737373" }} tickLine={false} axisLine={false} />
                    <YAxis stroke="#A3A3A3" tick={{ fontSize: 10, fill: "#737373" }} tickLine={false} axisLine={false} width={30} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 0, fontSize: 12 }}
                      formatter={(v, k) => [v, t(`admin.revenue.legend.${k}`)]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="square" />
                    <Bar dataKey="total" name={t("admin.revenue.legend.total")} fill="#C4A252">
                      {data.lead_conversion.by_source.map((entry, i) => (
                        <Cell key={`bar-${i}`} fill={SOURCE_COLORS[entry.source] || "#737373"} fillOpacity={0.4} />
                      ))}
                    </Bar>
                    <Bar dataKey="converted" name={t("admin.revenue.legend.converted")} fill="#15803D">
                      {data.lead_conversion.by_source.map((entry, i) => (
                        <Cell key={`bar2-${i}`} fill={SOURCE_COLORS[entry.source] || "#737373"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {data.lead_conversion.by_source.map((s) => (
                  <div key={s.source} className="flex justify-between border-b border-[#F3EFE9] py-1" data-testid={`conv-row-${s.source}`}>
                    <span className="text-[#0A0A0A] capitalize">{s.source.replace(/_/g, " ")}</span>
                    <span className="text-[#737373]">
                      <span className="font-semibold text-[#0A0A0A]">{s.converted}/{s.total}</span> · {s.pct}%
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quick links */}
      <div className="mt-8 text-center text-xs text-[#737373]" data-testid="revenue-quick-links">
        <Clock className="w-3 h-3 inline mr-1" />
        {t("admin.revenue.drill_in")}: <button onClick={() => nav("/back-office")} className="underline hover:text-[#8B1C22] mx-1">{t("admin.revenue.drill.backoffice")}</button> ·
        <button onClick={() => nav("/admin/leads")} className="underline hover:text-[#8B1C22] mx-1">{t("admin.nav.leads")}</button> ·
        <button onClick={() => nav("/admin/referrals")} className="underline hover:text-[#8B1C22] mx-1">{t("admin.nav.referrals")}</button>
      </div>
    </div>
  );
};

export default RevenueDashboard;
